import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { SaveIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import Loader from './loader'

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 font-semibold leading-6 data-[state=open]:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 data-[state=open]:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 data-[state=open]:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        green: 'bg-green-500/70 text-white hover:bg-green-500 data-[state=open]:bg-green-500',
        orange: 'bg-orange-500/70 text-white hover:bg-orange-500 data-[state=open]:bg-orange-500',
        blue: 'bg-blue-500/70 text-white hover:bg-blue-500 data-[state=open]:bg-blue-500',
        purple: 'bg-purple-500/70 text-white hover:bg-purple-500 data-[state=open]:bg-purple-500',
        orangeOutline: 'border border-orange-500/70 text-orange-500 hover:bg-orange-500/20 bg-orange-500/10 font-semibold data-[state=open]:bg-orange-500/20',
        gray: 'bg-gray-400/70 text-white hover:bg-gray-400 data-[state=open]:bg-gray-400',
        primaryLight: 'bg-primary/10 text-primary hover:bg-primary/20 data-[state=open]:bg-primary/20',
        destructiveOutline: 'border border-destructive/70 text-destructive hover:bg-destructive/20 data-[state=open]:bg-destructive/20',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-8 rounded-md px-2 text-xs',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = {
  asChild?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, type = 'button', size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'

  return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} type={type} {...props} />
})
Button.displayName = 'Button'

const SaveButton = React.forwardRef<HTMLButtonElement, ButtonProps & { isLoading?: boolean; icon?: React.ReactNode }>(
  ({ children, className, variant, type = 'button', size, asChild = false, isLoading, icon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    const childrenWithIcon = (
      <>
        {isLoading ? <Loader className="size-5" fill="#ffffff" /> : (icon ?? <SaveIcon className="size-5" />)}
        <span>{children}</span>
      </>
    )

    return (
      <Comp ref={ref} className={cn('space-x-2', buttonVariants({ variant, size, className }))} type={type} {...props}>
        {childrenWithIcon}
      </Comp>
    )
  },
)
SaveButton.displayName = 'SaveButton'

export { Button, SaveButton, buttonVariants }
